/**
 * 互动故事数据结构验证器
 * 确保AI输出的JSON符合编辑器要求的标准格式
 */
export class InteractiveStorySchema {
  
  /**
   * 完整的JSON Schema定义
   */
  static getSchema() {
    return {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      title: "互动故事数据结构",
      description: "用于AI生成的互动故事标准数据格式",
      required: ["outline", "characters", "chapters"],
      additionalProperties: true,
      
      properties: {
        // 故事大纲
        outline: {
          type: "object",
          title: "故事大纲",
          required: ["title", "summary", "mainPlot", "mainConflict"],
          properties: {
            title: {
              type: "string",
              minLength: 3,
              maxLength: 50,
              description: "故事标题"
            },
            summary: {
              type: "string", 
              minLength: 50,
              maxLength: 500,
              description: "故事摘要"
            },
            mainPlot: {
              type: "string",
              minLength: 30,
              maxLength: 300,
              description: "主线剧情"
            },
            mainConflict: {
              type: "string",
              minLength: 20,
              maxLength: 200,
              description: "核心冲突"
            },
            themes: {
              type: "array",
              items: { type: "string" },
              maxItems: 5,
              description: "故事主题"
            }
          }
        },
        
        // 角色定义
        characters: {
          type: "array",
          title: "角色列表",
          minItems: 1,
          maxItems: 10,
          items: {
            type: "object",
            required: ["id", "name", "role", "description", "appearance", "background", "personality", "imagePrompt", "languageStyle"],
            properties: {
              id: {
                type: "string",
                pattern: "^char-\\d+$",
                description: "角色唯一标识"
              },
              name: {
                type: "string",
                minLength: 1,
                maxLength: 30,
                description: "角色姓名"
              },
              role: {
                type: "string",
                enum: ["protagonist", "supporting", "antagonist", "narrator"],
                description: "角色类型"
              },
              description: {
                type: "string",
                minLength: 20,
                maxLength: 200,
                description: "角色简介"
              },
              appearance: {
                type: "string",
                minLength: 20,
                maxLength: 300,
                description: "外观描述"
              },
              background: {
                type: "string",
                minLength: 30,
                maxLength: 500,
                description: "背景故事"
              },
              personality: {
                type: "string",
                minLength: 20,
                maxLength: 300,
                description: "性格特征"
              },
              imagePrompt: {
                type: "string",
                minLength: 10,
                maxLength: 200,
                description: "AI生图提示词"
              },
              languageStyle: {
                type: "string",
                minLength: 5,
                maxLength: 100,
                description: "语言风格"
              },
              motivation: {
                type: "string",
                maxLength: 200,
                description: "角色动机"
              },
              arc: {
                type: "string",
                maxLength: 300,
                description: "角色成长弧线"
              },
              relationships: {
                type: "string",
                maxLength: 300,
                description: "角色关系"
              }
            }
          }
        },
        
        // 章节结构
        chapters: {
          type: "array",
          title: "章节列表",
          minItems: 1,
          maxItems: 8,
          items: {
            type: "object",
            required: ["id", "title", "description", "status", "scenes"],
            properties: {
              id: {
                type: ["number", "string"],
                description: "章节标识"
              },
              title: {
                type: "string",
                minLength: 3,
                maxLength: 50,
                description: "章节标题"
              },
              description: {
                type: "string",
                minLength: 10,
                maxLength: 300,
                description: "章节描述"
              },
              status: {
                type: "string",
                enum: ["completed", "outline", "draft"],
                description: "章节状态"
              },
              scenes: {
                type: "array",
                minItems: 1,
                maxItems: 20,
                items: {
                  type: "object",
                  required: ["id", "title", "description", "content", "imagePrompt", "hasChoices", "background", "mood", "timeOfDay", "location"],
                  properties: {
                    id: {
                      type: "string",
                      pattern: "^scene-\\d+-\\d+$",
                      description: "镜头唯一标识"
                    },
                    title: {
                      type: "string",
                      minLength: 2,
                      maxLength: 50,
                      description: "镜头标题"
                    },
                    description: {
                      type: "string",
                      minLength: 5,
                      maxLength: 200,
                      description: "镜头简介"
                    },
                    content: {
                      type: "string",
                      minLength: 100,
                      maxLength: 3000,
                      description: "镜头详细内容"
                    },
                    imagePrompt: {
                      type: "string",
                      minLength: 10,
                      maxLength: 300,
                      description: "镜头AI生图提示词"
                    },
                    hasChoices: {
                      type: "boolean",
                      description: "是否包含选择分支"
                    },
                    choices: {
                      type: "array",
                      maxItems: 5,
                      items: {
                        type: "object",
                        required: ["id", "text", "targetNodeId", "path", "consequence"],
                        properties: {
                          id: {
                            type: "string",
                            description: "选择唯一标识"
                          },
                          text: {
                            type: "string",
                            minLength: 5,
                            maxLength: 100,
                            description: "选择文本"
                          },
                          targetNodeId: {
                            type: "string",
                            description: "目标镜头ID"
                          },
                          path: {
                            type: "string",
                            description: "选择路径标识"
                          },
                          consequence: {
                            type: "string",
                            minLength: 5,
                            maxLength: 200,
                            description: "选择后果"
                          },
                          emotionalImpact: {
                            type: "string",
                            maxLength: 100,
                            description: "情感影响"
                          }
                        }
                      }
                    },
                    dialogues: {
                      type: "array",
                      maxItems: 20,
                      items: {
                        type: "object",
                        required: ["character", "content", "emotion"],
                        properties: {
                          character: {
                            type: "string",
                            minLength: 1,
                            maxLength: 30,
                            description: "说话角色"
                          },
                          content: {
                            type: "string",
                            minLength: 1,
                            maxLength: 500,
                            description: "对话内容"
                          },
                          emotion: {
                            type: "string",
                            enum: ["angry", "sad", "happy", "surprised", "fear", "disgust", "neutral", "excited", "confused", "determined"],
                            description: "情绪状态"
                          },
                          subtext: {
                            type: "string",
                            maxLength: 200,
                            description: "潜台词"
                          }
                        }
                      }
                    },
                    background: {
                      type: "string",
                      minLength: 5,
                      maxLength: 200,
                      description: "环境背景"
                    },
                    mood: {
                      type: "string",
                      minLength: 3,
                      maxLength: 50,
                      description: "镜头氛围"
                    },
                    timeOfDay: {
                      type: "string",
                      minLength: 2,
                      maxLength: 30,
                      description: "时间设定"
                    },
                    location: {
                      type: "string",
                      minLength: 2,
                      maxLength: 100,
                      description: "地点描述"
                    },
                    keyEvents: {
                      type: "array",
                      items: { type: "string" },
                      maxItems: 10,
                      description: "关键事件"
                    },
                    stateChanges: {
                      type: "object",
                      properties: {
                        characterMood: { type: "string" },
                        relationships: { type: "string" },
                        worldState: { type: "string" }
                      },
                      description: "状态变化"
                    }
                  }
                }
              },
              endings: {
                type: "array",
                items: {
                  type: "object",
                  required: ["id", "title", "description"],
                  properties: {
                    id: { type: "string" },
                    title: { type: "string" },
                    description: { type: "string" },
                    sceneId: { type: "string" }
                  }
                }
              }
            }
          }
        },
        
        // 可选的故事元数据
        storyMetadata: {
          type: "object",
          properties: {
            estimatedPlayTime: {
              type: "string",
              description: "预计游玩时长"
            },
            difficultyLevel: {
              type: "string",
              enum: ["simple", "moderate", "complex"],
              description: "复杂度等级"
            },
            emotionalTone: {
              type: "string",
              description: "情感基调"
            },
            targetAudience: {
              type: "string",
              description: "目标受众"
            },
            contentWarnings: {
              type: "array",
              items: { type: "string" },
              description: "内容警告"
            }
          }
        },
        
        // 分支点（可选）
        branchPoints: {
          type: "array",
          items: {
            type: "object",
            properties: {
              sceneId: { type: "string" },
              choices: {
                type: "array",
                items: {
                  type: "object",
                  properties: {
                    text: { type: "string" },
                    targetSceneId: { type: "string" },
                    path: { type: "string" }
                  }
                }
              }
            }
          }
        },
        
        // 最终结局（可选）
        finalEndings: {
          type: "array",
          items: {
            type: "object",
            properties: {
              id: { type: "string" },
              title: { type: "string" },
              description: { type: "string" },
              sceneId: { type: "string" }
            }
          }
        }
      }
    }
  }
  
  /**
   * 验证数据结构
   */
  static validate(data) {
    const errors = []
    const warnings = []
    
    try {
      // 基本结构验证
      if (!data || typeof data !== 'object') {
        errors.push('数据必须是对象')
        return { isValid: false, errors, warnings }
      }
      
      // 验证必需字段
      const requiredFields = ['outline', 'characters', 'chapters']
      for (const field of requiredFields) {
        if (!data[field]) {
          errors.push(`缺少必需字段: ${field}`)
        }
      }
      
      // 验证outline
      if (data.outline) {
        const outlineResult = this.validateOutline(data.outline)
        errors.push(...outlineResult.errors)
        warnings.push(...outlineResult.warnings)
      }
      
      // 验证characters
      if (data.characters) {
        const charactersResult = this.validateCharacters(data.characters)
        errors.push(...charactersResult.errors)
        warnings.push(...charactersResult.warnings)
      }
      
      // 验证chapters
      if (data.chapters) {
        const chaptersResult = this.validateChapters(data.chapters)
        errors.push(...chaptersResult.errors)
        warnings.push(...chaptersResult.warnings)
      }
      
      return {
        isValid: errors.length === 0,
        errors,
        warnings,
        stats: this.generateStats(data)
      }
      
    } catch (error) {
      errors.push(`验证过程出错: ${error.message}`)
      return { isValid: false, errors, warnings }
    }
  }
  
  /**
   * 验证故事大纲
   */
  static validateOutline(outline) {
    const errors = []
    const warnings = []
    
    const requiredFields = ['title', 'summary', 'mainPlot', 'mainConflict']
    for (const field of requiredFields) {
      if (!outline[field] || typeof outline[field] !== 'string') {
        errors.push(`outline.${field} 必须是非空字符串`)
      }
    }
    
    // 长度检查
    if (outline.title && outline.title.length < 3) {
      warnings.push('故事标题过短，建议至少3个字符')
    }
    if (outline.summary && outline.summary.length < 50) {
      warnings.push('故事摘要过短，建议至少50个字符')
    }
    
    return { errors, warnings }
  }
  
  /**
   * 验证角色列表
   */
  static validateCharacters(characters) {
    const errors = []
    const warnings = []
    
    if (!Array.isArray(characters)) {
      errors.push('characters 必须是数组')
      return { errors, warnings }
    }
    
    if (characters.length === 0) {
      errors.push('至少需要一个角色')
    }
    
    // 检查主角
    const protagonists = characters.filter(char => char.role === 'protagonist')
    if (protagonists.length === 0) {
      warnings.push('建议至少有一个主角角色')
    }
    
    characters.forEach((char, index) => {
      const requiredFields = ['id', 'name', 'role', 'description', 'appearance', 'background', 'personality', 'imagePrompt', 'languageStyle']
      for (const field of requiredFields) {
        if (!char[field] || typeof char[field] !== 'string') {
          errors.push(`characters[${index}].${field} 必须是非空字符串`)
        }
      }
      
      // ID格式检查
      if (char.id && !char.id.match(/^char-\d+$/)) {
        warnings.push(`characters[${index}].id 建议使用格式 'char-数字'`)
      }
      
      // 角色类型检查
      if (char.role && !['protagonist', 'supporting', 'antagonist', 'narrator'].includes(char.role)) {
        warnings.push(`characters[${index}].role 应该是 protagonist/supporting/antagonist/narrator 之一`)
      }
    })
    
    return { errors, warnings }
  }
  
  /**
   * 验证章节列表
   */
  static validateChapters(chapters) {
    const errors = []
    const warnings = []
    
    if (!Array.isArray(chapters)) {
      errors.push('chapters 必须是数组')
      return { errors, warnings }
    }
    
    if (chapters.length === 0) {
      errors.push('至少需要一个章节')
    }
    
    // 检查完整章节
    const completedChapters = chapters.filter(ch => ch.status === 'completed')
    if (completedChapters.length === 0) {
      warnings.push('建议至少有一个完整的章节')
    }
    
    chapters.forEach((chapter, chapterIndex) => {
      // 验证章节基本字段
      const requiredFields = ['id', 'title', 'description', 'status', 'scenes']
      for (const field of requiredFields) {
        if (field === 'scenes') {
          if (!Array.isArray(chapter[field])) {
            errors.push(`chapters[${chapterIndex}].scenes 必须是数组`)
          }
        } else if (!chapter[field]) {
          errors.push(`chapters[${chapterIndex}].${field} 是必需的`)
        }
      }
      
      // 验证场景
      if (Array.isArray(chapter.scenes)) {
        chapter.scenes.forEach((scene, sceneIndex) => {
          const sceneResult = this.validateScene(scene, chapterIndex, sceneIndex)
          errors.push(...sceneResult.errors)
          warnings.push(...sceneResult.warnings)
        })
      }
    })
    
    return { errors, warnings }
  }
  
  /**
   * 验证单个场景
   */
  static validateScene(scene, chapterIndex, sceneIndex) {
    const errors = []
    const warnings = []
    
    const requiredFields = ['id', 'title', 'description', 'content', 'imagePrompt', 'hasChoices', 'background', 'mood', 'timeOfDay', 'location']
    for (const field of requiredFields) {
      if (field === 'hasChoices') {
        if (typeof scene[field] !== 'boolean') {
          errors.push(`chapters[${chapterIndex}].scenes[${sceneIndex}].hasChoices 必须是布尔值`)
        }
      } else if (!scene[field] || typeof scene[field] !== 'string') {
        errors.push(`chapters[${chapterIndex}].scenes[${sceneIndex}].${field} 必须是非空字符串`)
      }
    }
    
    // ID格式检查
    if (scene.id && !scene.id.match(/^scene-\d+-\d+$/)) {
      warnings.push(`场景ID建议使用格式 'scene-章节号-场景号'`)
    }
    
    // 内容长度检查
    if (scene.content && scene.content.length < 100) {
      warnings.push(`章节${chapterIndex + 1}场景${sceneIndex + 1}内容过短，建议至少100字符`)
    }
    
    // 选择验证
    if (scene.hasChoices) {
      if (!Array.isArray(scene.choices) || scene.choices.length === 0) {
        errors.push(`章节${chapterIndex + 1}场景${sceneIndex + 1}标记有选择但choices数组为空`)
      } else {
        scene.choices.forEach((choice, choiceIndex) => {
          const requiredChoiceFields = ['id', 'text', 'targetNodeId', 'path', 'consequence']
          for (const field of requiredChoiceFields) {
            if (!choice[field] || typeof choice[field] !== 'string') {
              errors.push(`章节${chapterIndex + 1}场景${sceneIndex + 1}选择${choiceIndex + 1}.${field} 必须是非空字符串`)
            }
          }
        })
      }
    }
    
    // 对话验证
    if (Array.isArray(scene.dialogues)) {
      scene.dialogues.forEach((dialogue, dialogueIndex) => {
        if (!dialogue.character || !dialogue.content || !dialogue.emotion) {
          errors.push(`章节${chapterIndex + 1}场景${sceneIndex + 1}对话${dialogueIndex + 1} 缺少必需字段`)
        }
      })
    }
    
    return { errors, warnings }
  }
  
  /**
   * 生成数据统计
   */
  static generateStats(data) {
    const stats = {
      charactersCount: 0,
      chaptersCount: 0,
      scenesCount: 0,
      choicesCount: 0,
      dialoguesCount: 0,
      completedChapters: 0,
      averageSceneLength: 0,
      totalWordCount: 0
    }
    
    if (data.characters) {
      stats.charactersCount = data.characters.length
    }
    
    if (data.chapters) {
      stats.chaptersCount = data.chapters.length
      stats.completedChapters = data.chapters.filter(ch => ch.status === 'completed').length
      
      let totalContent = ''
      data.chapters.forEach(chapter => {
        if (Array.isArray(chapter.scenes)) {
          stats.scenesCount += chapter.scenes.length
          
          chapter.scenes.forEach(scene => {
            if (scene.content) {
              totalContent += scene.content
            }
            
            if (Array.isArray(scene.choices)) {
              stats.choicesCount += scene.choices.length
            }
            
            if (Array.isArray(scene.dialogues)) {
              stats.dialoguesCount += scene.dialogues.length
            }
          })
        }
      })
      
      stats.totalWordCount = totalContent.length
      stats.averageSceneLength = stats.scenesCount > 0 ? Math.round(stats.totalWordCount / stats.scenesCount) : 0
    }
    
    return stats
  }
}
